import React, { useState } from "react";
import "../styles/sessionModal.css";
import { getText, TextId } from "./Util/TextHelper";
import FocusIcon from "../assets/icons/focus-swirl.svg";
import RelaxIcon from "../assets/icons/relax-vector.svg";
import close from "../assets/icons/close.svg";
import CustomDropdown from "./CustomDropDown";
import { ZensoryModule } from "../ZensoryModule";
import { toLocalISOString, formatTimeInput } from "./Util/TimeUtil";
import SpinnerLoader from "./SpinnerLoader";
import { LOGGER, LogLevel } from "./Util/Logger";

const initialValues = {
  time: "",
  type: "Focus",
  length: "3",
};

const optionLengths = [
  { label: "3 Mins", value: "3" },
  { label: "10 Mins", value: "10" },
  { label: "25 Mins", value: "25" },
];

const moods = {
  focus: "Focus",
  relax: "Relax",
};
const SessionModal = ({ onClose, isCreate = true, initialData, onSubmit }) => {
  // Convert calendar event data to form format when editing
  const convertCalendarDataToForm = (calendarData) => {
    if (!calendarData) return { ...initialValues };

    let time = "";
    let type = "Focus";
    let length = "3";

    // Extract time from calendar event
    if (calendarData?.start && calendarData?.start?.dateTime) {
      const date = new Date(calendarData?.start?.dateTime);
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      time = `${hours}:${minutes}`;
    } else if (calendarData.time) {
      time = calendarData.time;
    }

    if (calendarData.subject) {
      const subjectUpper = calendarData?.categories
      if (subjectUpper.includes("Zensory-RELAX")) {
        type = "Relax";
      } else if (subjectUpper.includes("Zensory-FOCUS")) {
        type = "Focus";
      }
    }

    if (calendarData.durationMinutes) {
      length = calendarData?.durationMinutes.toString();
    } else if (calendarData.length) {
      length = calendarData.length.toString();
    }
    return {
      time,
      type,
      length,
    };
  };

  const [sessionData, setSessionData] = useState(
    isCreate ? initialValues : convertCalendarDataToForm(initialData)
  );
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    if (isCreate) {
      setSessionData({
        time: "",
        type: "Focus",
        length: "3",
      });
    } else {
      setSessionData({ ...initialValues, ...initialData });
    }
  };

  const handleSessionTimeOnChange = (e) => {
    const input = e.target.value;
    const formattedInput = formatTimeInput(input);

    if (formattedInput.length > 5) {
      return;
    }

    setSessionData((prevData) => ({ ...prevData, time: formattedInput }));

    if (!formattedInput.trim()) {
      setError("Session time cannot be empty.");
    } else {
      const timeRegex = /^(?:[01]?[0-9]|2[0-3]):[0-5][0-9]$/;
      if (!timeRegex.test(formattedInput)) {
        setError("Enter a valid time in HH:MM format (e.g., 09:30).");
      } else {
        setError("");
      }
    }
  };

  const handleSessionTimeOnBlur = () => {
    const timeValue = sessionData.time;
    const formattedInput = formatTimeInput(timeValue);

    // Update the field value if it was reformatted
    if (formattedInput !== timeValue) {
      setSessionData((prevData) => ({ ...prevData, time: formattedInput }));
    }

    if (!formattedInput.trim()) {
      setError("Session time cannot be empty.");
    } else {
      const timeRegex = /^(?:[01]?[0-9]|2[0-3]):[0-5][0-9]$/;
      if (!timeRegex.test(formattedInput)) {
        setError("Enter a valid time in HH:MM format (e.g., 09:30).");
      } else {
        setError("");
      }
    }
  };

  const handleSessionLengthOnChange = (value) => {
    setSessionData({ ...sessionData, length: value });
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (error || loading) return; // Don't submit if there's an error or already loading
    if (!sessionData.time.trim()) {
      setError("Session time cannot be empty.");
      return;
    }
    setError("");
    setLoading(true); // Set loading to true when starting the API call

    try {
      const userId = ZensoryModule.getAuth().getCurrentUser();
      if (!userId) {
        LOGGER.log(LogLevel.DEBUG, `No authenticated user found. userId: ${JSON.stringify(userId)}`);
        return;
      }

      const today = new Date();
      const [hours, minutes] = sessionData.time.split(":").map(Number);
      const sessionDateTime = new Date(today);
      sessionDateTime.setHours(hours, minutes, 0, 0);
      const startTime = toLocalISOString(sessionDateTime);
      const duration = parseInt(sessionData.length);
      const sessionType = sessionData.type.toUpperCase();

      if (isCreate) {
          await ZensoryModule.getCalenderApi().createCalenderschedule({
            userId: userId,
            session: {
              startTime: startTime,
              sessionType: sessionType,
              duration: duration,
            },
          });
        if (onSubmit) {
          onSubmit();
        }
      } else {
           await ZensoryModule.getCalenderApi().updateCalenderschedule({
            userId: userId,
            events: [
              {
                eventId: initialData.id,
                updateData: {
                  sessionType: sessionData.type,
                  start: {
                    dateTime: startTime,
                  },
                  durationMinutes: duration,
                },
              },
            ],
          });
        if (onSubmit) {
          onSubmit();
        }
      }

      onClose();
      setSessionData({
        time: "",
        type: moods.focus,
        length: "3",
      });
    } catch (err) {
      LOGGER.log(LogLevel.DEBUG, `Error submitting session: ${JSON.stringify(err)}`);
      setError("Failed to save session. Please try again.");
    } finally {
      setLoading(false); // Set loading to false when the API call is complete (success or error)
    }
  };

  const handleTypeChange = (type) => {
    setSessionData({ ...sessionData, type: type });
  };
  const handleOnClose = () => {
    onClose();
    resetForm();
  };
  const handleRemoveSession = async () => {
    if(isCreate){
        onClose()
        return
    }
    if (!initialData || !initialData.id) {
      setError("No session found for delete.");
      LOGGER.log(LogLevel.DEBUG, `No session ID provided for deletion. initialData: ${JSON.stringify(initialData)}`);
      return;
    }

    setError("");
    setLoading(true); // Set loading to true when starting delete operation

    try {
      const userId = ZensoryModule.getAuth().getCurrentUser();
      if (!userId) {
        LOGGER.log(LogLevel.DEBUG, `No authenticated user found for deletion. userId: ${JSON.stringify(userId)}`);
        return;
      }

      await ZensoryModule.getCalenderApi().deleteCalenderschedule({
        userId: userId,
        eventId: initialData.id,
      });
      onClose();
      if (onSubmit) {
        onSubmit(null, true);
      }
    } catch (error) {
      LOGGER.log(LogLevel.DEBUG, `Error deleting calendar session: ${JSON.stringify(error)}`);
    } finally {
      setLoading(false); // Set loading to false when the operation completes
    }
  };

  return (
    <div
      className="sche-modal-overlay"
      id="sessionModal"
      onClick={handleOnClose}
    >
      <div
        className="modal-content-wrapper custom-gradient-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-close">
          <div className="close-button" onClick={() => onClose()}>
            <img src={close} alt="close" />
          </div>
        </div>
        <div className="sche-modal-content-wrapper">
        <div className="sche-modal-content">
          <div className="modal-header">
            <h2 className="modal-title">
              {isCreate ? "Add session" : "Edit session"}
            </h2>
          </div>
          <form onSubmit={handleSubmit}>
            {error && <div className="error-message">{error}</div>}
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="sessionTime">
                  {getText(TextId.sessionTime)}
                </label>
                <div className="input-wrapper custom-input-gradient-border">
                  <input
                    type="text"
                    id="sessionTime"
                    className="input-field "
                    placeholder="Enter time"
                    value={sessionData?.time}
                    onChange={handleSessionTimeOnChange}
                    onBlur={handleSessionTimeOnBlur}
                    aria-invalid={!!error}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{getText(TextId.selectSessionTypeLabel)}</label>
                <div className="session-toggle custom-input-gradient-border">
                  <div
                    className={`toggle-indicator ${
                      sessionData?.type === moods.relax ? "right" : "left"
                    }`}
                  />
                  <button
                    type="button"
                    className={`toggle-button ${
                      sessionData?.type === moods.focus ? "active" : ""
                    }`}
                    onClick={() => handleTypeChange(moods.focus)}
                  >
                    <img src={FocusIcon} alt={"focusIcon"} />
                    {getText(TextId.sessionTypeFocus)}
                  </button>
                  <button
                    type="button"
                    className={`toggle-button ${
                      sessionData?.type === moods.relax ? "active" : ""
                    }`}
                    onClick={() => handleTypeChange(moods.relax)}
                  >
                    <img src={RelaxIcon} alt={"relaxIcon"} />
                    {getText(TextId.sessionTypeRelax)}
                  </button>
                </div>
              </div>

              <div className="form-selct">
                <div className="form-group half-width">
                  <CustomDropdown
                    options={optionLengths}
                    value={sessionData?.length}
                    label={getText(TextId.selectSessionLengthLabel)}
                    onChange={handleSessionLengthOnChange}
                    classNameInput={"custom-input-gradient-border"}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="submit"
                disabled={loading}
                className={`action-button  ${
                  !error && sessionData?.time !== ""
                    ? "action-button-active custom-input-gradient-border"
                    : "disable-submitBtn"
                } ${loading ? "p-25" : ""}`}
              >
                {loading ? (
                    <SpinnerLoader size={16} />
                ) : (
                  isCreate
                    ? getText(TextId.createAdd)
                    : getText(TextId.createUpdate)
                )}
              </button>
              <span 
                className={`cancelButton`} 
                onClick={handleRemoveSession}
                style={{ pointerEvents: loading ? 'none' : 'auto' }}
                disabled={loading}
              >
                {getText(TextId.cancelLinkText)}
              </span>
            </div>
          </form>
        </div>
        </div>
      </div>
    </div>
  );
};

export default SessionModal;